import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { map, Subject, switchMap, take, tap } from 'rxjs';

import type { DynamicFormConfig } from '@core/ask-hr-dynamic-forms-types';
import { AskTicketsActions } from '@core/ask-hr-tickets-data';
import type { CreateAskTicket } from '@core/ask-hr-tickets-types';
import type { AskTicketStatus } from '@core/ask-hr-types';
import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { DialogService, NotificationsController } from '@core/user-interface-legacy';

import { TicketsDataService } from './tickets-data.service';

@Injectable({ providedIn: 'root' })
export class TicketService {
  private readonly dialogService = inject(DialogService);
  private readonly ticketsDataService = inject(TicketsDataService);
  private readonly notificationsCtrl = inject(NotificationsController);
  private readonly i18n = inject(I18nService);
  private readonly actions$ = inject(Actions);

  public openTicket$ = new Subject<number>();

  public confirmCancel(ticketId: number) {
    return this.dialogService
      .error({
        title: this.i18n.translate('tlc.ask_hr.request_dialog.cancel_request'),
        message: this.i18n.translate('tlc.ask_hr.request_dialog.cancel_request_description'),
        agreeLabel: this.i18n.translate('tlc.ask_hr.request_dialog.confirm_cancel'),
      })
      .pipe(
        take(1),
        filterBoolean,
        switchMap(isSuccess =>
          isSuccess
            ? this.ticketsDataService.cancelTicket(ticketId).pipe(map(result => result.isSuccess))
            : [false],
        ),
      );
  }

  public getViewsRefreshEvents$() {
    return this.actions$.pipe(
      ofType(
        AskTicketsActions.createOnePessimisticSuccess,
        AskTicketsActions.updateOnePessimisticSuccess,
        AskTicketsActions.moveSuccess,
        AskTicketsActions.cancelSuccess,
      ),
      map(() => void 0),
    );
  }

  public createTicket(template: CreateAskTicket<DynamicFormConfig>) {
    return this.ticketsDataService.createTicket(template).pipe(
      tap(result => {
        if (result.isSuccess) {
          this.notifyTicketCreated(result.success!.id);
        }
      }),
    );
  }

  public moveTicket(ticketId: number, status: AskTicketStatus) {
    return this.ticketsDataService.moveTicket(ticketId, { status }).pipe(
      tap(result => {
        if (result.isSuccess && status === 'closed') {
          this.notifyTicketClosed();
        }
      }),
    );
  }

  private notifyTicketCreated(id: number) {
    this.notificationsCtrl
      .success({
        title: this.i18n.translate('tlc.ask_hr.request_dialog.ticket_created_notification'),
        topButtonLabel: this.i18n.translate(
          'tlc.ask_hr.request_dialog.ticket_created_notification_cta',
        ),
      })
      .topButtonClick()
      .subscribe(() => this.openTicket$.next(id));
  }

  private notifyTicketClosed() {
    this.notificationsCtrl.success({
      title: this.i18n.translate('tlc.ask_hr.request_dialog.ticket_closed_notification'),
    });
  }
}
